/**
 * Landing screen for the email-change confirmation link (SVC-1).
 *
 * Hosted at `/confirm-email-change?token=...`. The token only works from the
 * session that requested the change, so a signed-out visitor is asked to sign
 * in with the current address and open the link again.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import { userProfileAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import {
  ConfirmEmailChangeStatus,
  initialStatus,
  readTokenParam,
  runConfirmEmailChange,
} from './confirmEmailChange';
import { brand } from '../../design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  error: '#DC2626',
  success: '#10B981',
  text: { primary: '#111827', secondary: '#4B5563' },
};

export default function ConfirmEmailChangeScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { phase, logout } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();
  const token = readTokenParam(params.token);
  const [status, setStatus] = useState<ConfirmEmailChangeStatus>(() => initialStatus(token, phase));
  const submittedRef = useRef(false);

  useEffect(() => {
    if (submittedRef.current) return;
    const next = initialStatus(token, phase);
    if (next !== 'confirming') {
      setStatus(next);
      return;
    }
    submittedRef.current = true;
    setStatus('confirming');
    void runConfirmEmailChange(token, userProfileAPI.confirmEmailChange).then(setStatus);
  }, [phase, token]);

  const signedIn = phase !== 'unauthenticated' && phase !== 'loading';
  const onPrimary = () => {
    if (status === 'success' && signedIn) {
      // Sessions were revoked server-side; logout() clears local auth state
      // and routes to sign-in.
      void logout();
    } else if (!signedIn) {
      router.replace(ROUTES.PUBLIC_LOGIN as any);
    } else {
      router.replace(ROUTES.TABS_ROOT as any);
    }
  };

  const busy = status === 'waiting' || status === 'confirming';
  const ok = status === 'success';
  const title = busy
    ? t('emailChange.confirming')
    : ok
      ? t('emailChange.successTitle')
      : status === 'needs_login'
        ? t('emailChange.needsLoginTitle')
        : status === 'invalid'
          ? t('emailChange.invalidTitle')
          : t('emailChange.failedTitle');
  const body = busy
    ? ''
    : ok
      ? t('emailChange.successBody')
      : status === 'needs_login'
        ? t('emailChange.needsLoginBody')
        : status === 'invalid'
          ? t('emailChange.invalidBody')
          : t('emailChange.failedBody');

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: COLORS.surface }}>
      <View className="flex-1 items-center justify-center px-6">
        <View style={{ width: '100%', maxWidth: 440 }}>
          <Card style={{ backgroundColor: COLORS.background, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border }}>
            <CardContent style={{ padding: 32, alignItems: 'center' }} testID="confirm-email-change">
              {busy ? (
                <ActivityIndicator size="large" color={COLORS.primary} style={{ marginBottom: 16 }} />
              ) : (
                <MaterialIcons
                  name={ok ? 'check-circle' : 'error-outline'}
                  size={40}
                  color={ok ? COLORS.success : COLORS.error}
                  style={{ marginBottom: 16 }}
                />
              )}
              <Text
                testID="confirm-email-change-title"
                style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: '700', marginBottom: 8, textAlign: 'center' }}
              >
                {title}
              </Text>
              {body ? (
                <Text style={{ color: COLORS.text.secondary, fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
                  {body}
                </Text>
              ) : null}
              {!busy && (
                <Button variant="default" size="default" onPress={onPrimary} style={{ width: '100%' }}>
                  <Text className="text-white font-semibold">
                    {ok || !signedIn ? t('emailChange.signIn') : t('emailChange.backToApp')}
                  </Text>
                </Button>
              )}
            </CardContent>
          </Card>
        </View>
      </View>
    </SafeAreaView>
  );
}
